import type { MarketOpportunity, Side } from "@/lib/whale-watch/whale-watch-shared";
import type { DisplayPlayerProp } from "@/components/optimizer/utils-v2";

export const DISMISSED_BETS_VERSION = 2;
export const MAX_DISMISSED_BETS = 1_000;
export const DISMISSED_BET_EVENT_START_GRACE_MS = 15 * 60 * 1_000;
export const DISMISSED_BETS_REQUEST_TIMEOUT_MS = 10_000;

const FINGERPRINT_PREFIX = "dismiss:v1";
const NO_LINE_TOKEN = "no-line";
const EMPTY_TOKEN = "none";
const UNIX_SECONDS_TIMESTAMP_UPPER_BOUND = 10_000_000_000;

type FingerprintValue = string | number | null | undefined;

export interface CanonicalFingerprintInput {
  sport: FingerprintValue;
  eventId: FingerprintValue;
  entityId: FingerprintValue;
  market: FingerprintValue;
  line?: FingerprintValue;
  side: FingerprintValue;
}

export const DISMISSED_ROW_SIDE = "line";

export type DismissedBetMetadata = {
  tool?: string;
  sport?: string;
  league?: string;
  eventId?: string | number;
  eventName?: string;
  entityId?: string | number;
  entityName?: string;
  market?: string;
  line?: string | number | null;
  side?: string;
  selection?: string;
  sportsbook?: string;
  eventStart?: number;
};

export type DismissedBetRecord = {
  dismissedAt: number;
  meta?: DismissedBetMetadata;
};

export type DismissedBetsMap = Record<string, DismissedBetRecord>;

export type DismissedBetsState = {
  hidden: DismissedBetsMap;
};

export type DismissedBetsPersistedState = {
  v: typeof DISMISSED_BETS_VERSION;
  savedAt: string;
  state: DismissedBetsState;
};

function normalizeToken(value: FingerprintValue): string | null {
  if (value === null || value === undefined) return null;

  if (typeof value === "number") {
    if (!Number.isFinite(value)) return null;
    return normalizeNumericToken(value);
  }

  const normalized = value
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9.+-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-{2,}/g, "-");

  return normalized.length > 0 ? normalized : null;
}

function normalizeNumericToken(value: number): string {
  const rounded = Math.round((Object.is(value, -0) ? 0 : value) * 1_000) / 1_000;
  return Number.isInteger(rounded)
    ? String(rounded)
    : rounded.toFixed(3).replace(/0+$/g, "").replace(/\.$/g, "");
}

function normalizeLine(value: FingerprintValue): string {
  if (value === null || value === undefined || value === "") return NO_LINE_TOKEN;

  if (typeof value === "number") {
    return Number.isFinite(value) ? normalizeNumericToken(value) : NO_LINE_TOKEN;
  }

  const numeric = Number(value);
  if (Number.isFinite(numeric) && value.trim() !== "") {
    return normalizeNumericToken(numeric);
  }

  return normalizeToken(value) ?? NO_LINE_TOKEN;
}

function normalizeSide(value: FingerprintValue): string | null {
  const token = normalizeToken(value);
  if (!token) return null;

  switch (token) {
    case "o":
    case "over":
      return "over";
    case "u":
    case "under":
      return "under";
    case "h":
    case "home":
      return "home";
    case "a":
    case "away":
      return "away";
    case "y":
    case "yes":
      return "yes";
    case "n":
    case "no":
      return "no";
    default:
      return token;
  }
}

function normalizeMarket(value: FingerprintValue): string | null {
  const token = normalizeToken(value);
  if (!token) return null;

  return token
    .replace(/^player-/, "")
    .replace(/^alternate-/, "")
    .replace(/-alternate$/, "");
}

function hasOwnRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function coercePositiveEpochTimestampMs(timestamp: number): number | undefined {
  if (!Number.isFinite(timestamp) || timestamp <= 0) return undefined;

  return timestamp < UNIX_SECONDS_TIMESTAMP_UPPER_BOUND
    ? timestamp * 1_000
    : timestamp;
}

export function coerceDismissedBetEventStartMs(value: unknown): number | undefined {
  if (value instanceof Date) {
    const timestamp = value.getTime();
    return Number.isFinite(timestamp) && timestamp > 0 ? timestamp : undefined;
  }

  if (typeof value === "number") {
    return coercePositiveEpochTimestampMs(value);
  }

  if (typeof value !== "string") return undefined;

  const trimmed = value.trim();
  if (!trimmed) return undefined;

  const numericTimestamp = Number(trimmed);
  if (Number.isFinite(numericTimestamp) && numericTimestamp > 0) {
    return coercePositiveEpochTimestampMs(numericTimestamp);
  }

  const parsedTimestamp = Date.parse(trimmed);
  return Number.isFinite(parsedTimestamp) && parsedTimestamp > 0 ? parsedTimestamp : undefined;
}

function coerceMetadata(value: unknown): DismissedBetMetadata | undefined {
  if (!hasOwnRecord(value)) return undefined;

  const meta: DismissedBetMetadata = {};
  const copyString = (key: keyof DismissedBetMetadata) => {
    const candidate = value[key];
    if (typeof candidate === "string" && candidate.trim().length > 0) {
      meta[key] = candidate as never;
    }
  };
  const copyScalar = (key: keyof DismissedBetMetadata) => {
    const candidate = value[key];
    if (
      typeof candidate === "string" ||
      (typeof candidate === "number" && Number.isFinite(candidate)) ||
      candidate === null
    ) {
      meta[key] = candidate as never;
    }
  };

  copyString("tool");
  copyString("sport");
  copyString("league");
  copyString("eventName");
  copyString("entityName");
  copyString("market");
  copyString("side");
  copyString("selection");
  copyString("sportsbook");
  copyScalar("eventId");
  copyScalar("entityId");
  copyScalar("line");

  const eventStart = coerceDismissedBetEventStartMs(value.eventStart);
  if (eventStart !== undefined) {
    meta.eventStart = eventStart;
  }

  return Object.keys(meta).length > 0 ? meta : undefined;
}

function coerceDismissedBetRecord(value: unknown): DismissedBetRecord | null {
  if (!hasOwnRecord(value)) return null;
  const { dismissedAt } = value;
  if (typeof dismissedAt !== "number" || !Number.isFinite(dismissedAt) || dismissedAt <= 0) return null;

  const meta = coerceMetadata(value.meta);
  return meta ? { dismissedAt, meta } : { dismissedAt };
}

const TOKEN_LABEL_OVERRIDES: Record<string, string> = {
  mlb: "MLB",
  nba: "NBA",
  ncaab: "NCAAB",
  ncaaf: "NCAAF",
  cfb: "CFB",
  nfl: "NFL",
  nhl: "NHL",
  mls: "MLS",
  wnba: "WNBA",
  kbo: "KBO",
  ufc: "UFC",
};

const GENERIC_LEGACY_TOKENS = new Set([
  EMPTY_TOKEN,
  NO_LINE_TOKEN,
  "legacy",
  "fingerprint",
  "unknown",
  "unknown-league",
]);

function isOpaqueLegacyToken(token: string | null | undefined): boolean {
  if (!token) return true;
  if (GENERIC_LEGACY_TOKENS.has(token)) return true;
  if (/^\d+$/.test(token)) return true;
  if (/^[a-f0-9]{16,}$/i.test(token)) return true;
  return false;
}

function formatLegacyToken(token: string | null | undefined): string | undefined {
  if (!token || isOpaqueLegacyToken(token)) return undefined;

  const normalized = token.trim().toLowerCase();
  const override = TOKEN_LABEL_OVERRIDES[normalized];
  if (override) return override;

  const label = normalized
    .replace(/\+/g, " +")
    .replace(/-/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (character) => character.toUpperCase());

  return label.length > 0 ? label : undefined;
}

function formatLegacySide(token: string | null | undefined): string | undefined {
  const normalized = token?.trim().toLowerCase();
  if (!normalized || normalized === EMPTY_TOKEN) return undefined;

  if (normalized.includes("over")) return "Over";
  if (normalized.includes("under")) return "Under";
  if (normalized.includes("home")) return "Home";
  if (normalized.includes("away")) return "Away";
  if (normalized === "yes") return "Yes";
  if (normalized === "no") return "No";
  if (normalized.includes("lowhold-pair")) return "Low Hold Pair";
  if (normalized.includes("arbitrage-pair")) return "Arbitrage Pair";

  return formatLegacyToken(normalized);
}

function formatLegacyLine(token: string | null | undefined): string | undefined {
  if (!token || token === NO_LINE_TOKEN || token === EMPTY_TOKEN) return undefined;
  if (/^[+-]?\d+(?:\.\d+)?$/.test(token)) return token;
  return token.replace(/-/g, " ").trim();
}

function buildLegacySelection(
  entityName: string | undefined,
  side: string | undefined,
  line: string | undefined,
): string | undefined {
  const sideWithLine = [side, line].filter(Boolean).join(" ");
  const pieces = [entityName, sideWithLine].filter(Boolean);
  return pieces.length > 0 ? pieces.join(" · ") : undefined;
}

function compactMeta(meta: DismissedBetMetadata): DismissedBetMetadata | undefined {
  const compacted = Object.fromEntries(
    Object.entries(meta).filter(([, value]) => value !== undefined && value !== ""),
  ) as DismissedBetMetadata;

  return Object.keys(compacted).length > 0 ? compacted : undefined;
}

export function parseLegacyDismissedBetMetadata(fingerprint: string): DismissedBetMetadata | undefined {
  const tokens = fingerprint.split(":").map((token) => token.trim()).filter(Boolean);
  if (tokens[0] !== "dismiss" || tokens[1] !== "v1") return undefined;

  if (tokens[2] === "odds-screen-line") {
    const sport = formatLegacyToken(tokens[3]);
    const market = formatLegacyToken(tokens[6]) ?? formatLegacyToken(tokens[7]);
    const lineTitle = formatLegacyToken(tokens[9]);
    const lineSubtitle = formatLegacyLine(tokens[10]);
    const firstOutcomeLabel = tokens[11]?.split("+")[0]?.split("~")[1];
    const outcomeLabel = formatLegacyToken(firstOutcomeLabel);
    const entityName = lineTitle ?? outcomeLabel;

    return compactMeta({
      tool: "Odds Screen",
      sport,
      market,
      entityName,
      selection: [entityName, lineSubtitle].filter(Boolean).join(" ") || undefined,
    });
  }

  let cursor = 2;
  let tool: string | undefined;
  if (tokens[cursor] === "whale") {
    tool = "Whale Watch";
    cursor += 1;
  }

  const sport = formatLegacyToken(tokens[cursor]);
  const remaining = tokens.slice(cursor + 2);
  const sideToken = remaining.at(-1);
  const lineToken = remaining.at(-2);
  const marketToken = remaining.at(-3);
  const entityTokens = remaining.slice(0, -3);
  const entityToken = entityTokens.find((token) => !isOpaqueLegacyToken(token));
  const entityName = formatLegacyToken(entityToken);
  const market = formatLegacyToken(marketToken);
  const side = formatLegacySide(sideToken);
  const line = formatLegacyLine(lineToken);

  if (!tool) {
    const normalizedSide = sideToken?.toLowerCase() ?? "";
    if (normalizedSide.includes("lowhold-pair")) tool = "Low Hold";
    if (normalizedSide.includes("arbitrage-pair")) tool = "Arbitrage";
  }

  return compactMeta({
    tool: tool ?? "Saved Bet",
    sport,
    entityName,
    market,
    line,
    side,
    selection: buildLegacySelection(entityName, side, line),
  });
}

export function buildCanonicalFingerprint(input: CanonicalFingerprintInput): string | null {
  const sport = normalizeToken(input.sport);
  const eventId = normalizeToken(input.eventId);
  const entityId = normalizeToken(input.entityId);
  const market = normalizeMarket(input.market);
  const side = normalizeSide(input.side);

  if (!sport || !eventId || !entityId || !market || !side) {
    return null;
  }

  return [
    FINGERPRINT_PREFIX,
    sport,
    eventId,
    entityId,
    market,
    normalizeLine(input.line),
    side,
  ].join(":");
}

export function getOptimizerFingerprint(row: DisplayPlayerProp): string | null {
  return buildCanonicalFingerprint({
    sport: row.sport,
    eventId: row.game_id,
    entityId: row.player_name,
    market: row.stat_type ?? row.market_display,
    line: row.line,
    side: row.bet_type ?? row.selection_side,
  });
}

export function getWhaleWatchFingerprint(
  opportunity: MarketOpportunity,
  side: Side | null | undefined = opportunity.market.sides[0],
): string | null {
  if (!side) return null;

  return buildCanonicalFingerprint({
    sport: opportunity.sport || opportunity.league,
    eventId: opportunity.event_id,
    entityId: side.name,
    market: opportunity.market.market,
    line: side.line ?? opportunity.market.line,
    side: side.side,
  });
}

function canonicalizeMatchup(label: string): string {
  const atParts = label.split(/\s+@\s+/);
  const parts = atParts.length === 2 ? atParts : label.split(/\s+v(?:s\.?|\.)\s+/i);
  if (parts.length !== 2) return label;
  return parts
    .map((part) => part.trim())
    .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
    .join(" vs ");
}

const PLACEHOLDER_MATCHUP = "matchup tbd";

function stripTrailingLine(value: string): string {
  return value.replace(/\s+[+-]?\d+(?:\.\d+)?\s*$/, "");
}


export function isDismissedBetExpiredByEventStart(
  record: DismissedBetRecord,
  now = Date.now(),
): boolean {
  const eventStart = coerceDismissedBetEventStartMs(record.meta?.eventStart);
  if (eventStart === undefined || record.dismissedAt >= eventStart) return false;

  return now >= eventStart + DISMISSED_BET_EVENT_START_GRACE_MS;
}

export function pruneDismissedBetsMap(
  hidden: DismissedBetsMap,
  maxEntries = MAX_DISMISSED_BETS,
  now = Date.now(),
): DismissedBetsMap {
  const entries = Object.entries(hidden)
    .filter((entry): entry is [string, DismissedBetRecord] => {
      const [fingerprint, record] = entry;
      return typeof fingerprint === "string" &&
        fingerprint.length > 0 &&
        Boolean(record) &&
        Number.isFinite(record.dismissedAt) &&
        record.dismissedAt > 0 &&
        !isDismissedBetExpiredByEventStart(record, now);
    })
    .sort(([, a], [, b]) => b.dismissedAt - a.dismissedAt)
    .slice(0, maxEntries);

  return Object.fromEntries(entries);
}

export function coerceDismissedBetsPayload(value: unknown): DismissedBetsPersistedState | null {
  if (!hasOwnRecord(value) || value.v !== DISMISSED_BETS_VERSION) {
    return null;
  }

  const state = value.state;
  if (!hasOwnRecord(state)) return null;

  const rawHidden = hasOwnRecord(state.hidden) ? state.hidden : null;
  if (!rawHidden) return null;

  const hidden: DismissedBetsMap = {};
  for (const [fingerprint, rawRecord] of Object.entries(rawHidden)) {
    const record = coerceDismissedBetRecord(rawRecord);
    if (typeof fingerprint === "string" && fingerprint.length > 0 && record) {
      hidden[fingerprint] = record;
    }
  }

  const savedAt = typeof value.savedAt === "string"
    ? value.savedAt
    : new Date(0).toISOString();

  return {
    v: DISMISSED_BETS_VERSION,
    savedAt,
    state: {
      hidden: pruneDismissedBetsMap(hidden),
    },
  };
}

export function createDismissedBetsPayload(
  hidden: DismissedBetsMap,
  savedAt = new Date().toISOString(),
): DismissedBetsPersistedState {
  return {
    v: DISMISSED_BETS_VERSION,
    savedAt,
    state: {
      hidden: pruneDismissedBetsMap(hidden),
    },
  };
}

export function getDismissedBetCount(hidden: DismissedBetsMap): number {
  return Object.keys(hidden).length;
}

function metadataEqual(a: DismissedBetMetadata | undefined, b: DismissedBetMetadata | undefined): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  const aEntries = Object.entries(a);
  if (aEntries.length !== Object.keys(b).length) return false;
  return aEntries.every(([key, value]) => b[key as keyof DismissedBetMetadata] === value);
}

export function dismissedBetsMapsEqual(a: DismissedBetsMap, b: DismissedBetsMap): boolean {
  const aEntries = Object.entries(a);
  if (aEntries.length !== Object.keys(b).length) return false;
  return aEntries.every(([key, value]) => {
    const next = b[key];
    return Boolean(next) && next.dismissedAt === value.dismissedAt && metadataEqual(next.meta, value.meta);
  });
}

export type DismissedBetOp =
  | { type: "hide"; fingerprint: string; record: DismissedBetRecord }
  | { type: "restore"; fingerprint: string };

export function applyDismissedBetOps(
  hidden: DismissedBetsMap,
  ops: readonly DismissedBetOp[],
  now = Date.now(),
): DismissedBetsMap {
  const next = { ...hidden };
  for (const op of ops) {
    if (op.type === "hide") next[op.fingerprint] = op.record;
    else delete next[op.fingerprint];
  }
  return pruneDismissedBetsMap(next, MAX_DISMISSED_BETS, now);
}

export function coerceDismissedBetOps(value: unknown): DismissedBetOp[] | null {
  if (!Array.isArray(value)) return null;
  const ops = value.map((op): DismissedBetOp | null => {
    if (!hasOwnRecord(op) || typeof op.fingerprint !== "string" || op.fingerprint.length === 0) return null;
    if (op.type === "restore") return { type: "restore", fingerprint: op.fingerprint };
    const record = op.type === "hide" ? coerceDismissedBetRecord(op.record) : null;
    return record ? { type: "hide", fingerprint: op.fingerprint, record } : null;
  });
  return ops.every((op): op is DismissedBetOp => op !== null) ? ops : null;
}
